import type { Role } from "@/types/domain";

/**
 * Permission matrix derived from docs/ARCHITECTURE.md §9 + docs/DESIGN.md §3.1.
 *
 * Roles:
 *  - finance_admin   — full read + sign-off (Client Finance Lead)
 *  - accounts_user   — reports + exceptions read (Client Accounts Team)
 *  - developer       — admin incl. mapping_rules write (Born Digital support)
 *  - commercial      — reports read only (Born Digital commercial)
 */

export type Permission =
  | "reports.read"
  | "exceptions.read"
  | "exceptions.write"
  | "sync.read"
  | "sync.replay"
  | "admin.mapping.read"
  | "admin.mapping.write"
  | "admin.corporate.read"
  | "admin.corporate.write"
  | "admin.users.read"
  | "admin.users.write";

const MATRIX: Record<Role, ReadonlySet<Permission>> = {
  finance_admin: new Set<Permission>([
    "reports.read",
    "exceptions.read",
    "exceptions.write",
    "sync.read",
  ]),
  accounts_user: new Set<Permission>([
    "reports.read",
    "exceptions.read",
    "exceptions.write",
    "sync.read",
  ]),
  developer: new Set<Permission>([
    "reports.read",
    "exceptions.read",
    "exceptions.write",
    "sync.read",
    "sync.replay",
    "admin.mapping.read",
    "admin.mapping.write",
    "admin.corporate.read",
    "admin.corporate.write",
    "admin.users.read",
    "admin.users.write",
  ]),
  commercial: new Set<Permission>(["reports.read", "sync.read"]),
};

export function can(role: Role, permission: Permission): boolean {
  return MATRIX[role].has(permission);
}

export function canAny(role: Role, permissions: readonly Permission[]): boolean {
  return permissions.some((p) => can(role, p));
}

/** All roles, ordered for the dev role-switcher menu. */
export const ALL_ROLES: readonly Role[] = [
  "finance_admin",
  "accounts_user",
  "developer",
  "commercial",
];

export const ROLE_LABEL: Record<Role, string> = {
  finance_admin: "Finance Admin",
  accounts_user: "Accounts User",
  developer: "Developer",
  commercial: "Commercial",
};
